"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import type { Testimonial } from "@/lib/content/testimonials-bank";

// Carrousel de TOUS les avis clients (banque testimonials-bank.ts, la même que
// /avis), sous "Qui je suis" sur la page vitrine (demande Thibault
// 2026-09-27). Les avis les plus longs d'abord (tri fait côté serveur).
// Défilement natif avec aimantation (doigt, trackpad, flèches), sans
// dépendance. Texte long replié avec "Lire la suite".

type Avis = Pick<Testimonial, "author" | "context" | "date" | "quote" | "source"> & { translated?: boolean };
type Lang = "fr" | "en";

const MONTHS_EN: Record<string, string> = {
  janvier: "January", février: "February", mars: "March", avril: "April", mai: "May", juin: "June",
  juillet: "July", août: "August", septembre: "September", octobre: "October", novembre: "November", décembre: "December",
};

const CLAMP_CHARS = 420;

function Card({ avis, lang }: { avis: Avis; lang: Lang }) {
  const en = lang === "en";
  const date = avis.date && en ? avis.date.replace(/\p{L}+/u, (m) => MONTHS_EN[m.toLowerCase()] ?? m) : avis.date;
  const [open, setOpen] = useState(false);
  const long = avis.quote.length > CLAMP_CHARS;
  return (
    <figure className="flex h-full flex-col rounded-2xl border border-line bg-paper-pure p-6">
      <span className="flex items-center gap-0.5" aria-hidden>
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" strokeWidth={1.5} />
        ))}
      </span>
      <blockquote
        className={`mt-4 whitespace-pre-line leading-relaxed text-ink/85 ${long && !open ? "line-clamp-9" : ""}`}
      >
        &ldquo;{avis.quote}&rdquo;
      </blockquote>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-2 self-start text-sm text-ink/60 underline decoration-ink/30 underline-offset-4 hover:text-ink"
        >
          {en ? (open ? "Show less" : "Read more") : open ? "Réduire" : "Lire la suite"}
        </button>
      )}
      <figcaption className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-5 text-sm">
        <span>
          <span className="font-medium text-ink">{avis.author}</span>
          {avis.context && <span className="text-ink/60"> · {avis.context}</span>}
          {date && <span className="block text-xs text-ink/50">{date}</span>}
          {avis.translated && <span className="block text-xs italic text-ink/45">Translated from French</span>}
        </span>
        <span className="rounded-full border border-line px-2 py-0.5 text-xs text-ink/60">{avis.source}</span>
      </figcaption>
    </figure>
  );
}

export function ReviewsCarousel({ avis, lang = "fr" }: { avis: Avis[]; lang?: Lang }) {
  const en = lang === "en";
  const track = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-card]");
    el.scrollBy({ left: dir * ((card?.offsetWidth ?? el.clientWidth) + 16), behavior: "smooth" });
  };

  if (avis.length === 0) return null;

  return (
    <div className="mx-auto mt-14 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 px-4">
        <div>
          <h3 className="font-display text-3xl text-ink">{en ? "What my clients say" : "Ce qu'en disent mes clients"}</h3>
          <Link href={en ? "/en/avis" : "/avis"} className="mt-1 inline-block text-sm text-ink/60 underline decoration-ink/30 underline-offset-4 hover:text-ink">
            {en ? `See all ${avis.length} reviews` : `Voir les ${avis.length} avis`}
          </Link>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => scroll(-1)}
            aria-label={en ? "Previous review" : "Avis précédent"}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-paper-pure text-ink/70 hover:text-ink"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            aria-label={en ? "Next review" : "Avis suivant"}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-paper-pure text-ink/70 hover:text-ink"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        ref={track}
        className="mt-6 flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto scroll-smooth px-4 pb-4 [scrollbar-width:thin]"
      >
        {avis.map((a, i) => (
          <div
            key={`${a.author}-${i}`}
            data-card
            className="w-[85%] shrink-0 snap-start sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
          >
            <Card avis={a} lang={lang} />
          </div>
        ))}
      </div>
    </div>
  );
}
